import { MouseEvent, useEffect, useMemo, useState } from 'react'

import {
    Avatar,
    Box,
    ClickAwayListener,
    List,
    ListItem,
    Paper,
    Popover,
    withStyles
} from '@material-ui/core'

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import PeopleAltIcon from '@material-ui/icons/LockOutlined'

import { ReactComponent as PublicComments } from 'assets/icons/publicComments.svg'
import { ReactComponent as CommentsOff } from 'assets/icons/commentsOff.svg'

import {
    COMMENTS_STATUS_OPTIONS,
    useAuth,
    useNotification,
    useOrganization,
    useOrgFeatureFlags
} from 'hooks'
import { logToAnalytics, updateComments } from 'modules'

import { SpacedGroup } from 'UI/Components'
import { PlaybookType } from 'app/types'

const COMMENTS_LABEL = {
    on: 'Comments are public',
    off: 'Comments are off',
    anyoneInOrg: 'Comments are restricted'
}

type optionType = {
    value: Required<PlaybookType['commentsStatus']>
    label: string
    caption: string
    icon?: JSX.Element
}

const getCommentsOptions = (orgName: string, privateAccount: boolean) => [
    {
        icon: <PublicComments />,
        value: COMMENTS_STATUS_OPTIONS.on,
        label: 'Public comments',
        caption: 'Anyone can add, view, and reply to comments (default)'
    },
    ...(privateAccount
        ? []
        : [
              {
                  icon: (
                      <Avatar style={{ backgroundColor: '#F5F5F5', color: '#1C1B1F' }}>
                          <PeopleAltIcon />
                      </Avatar>
                  ),
                  value: COMMENTS_STATUS_OPTIONS.anyoneInOrg,
                  label: 'Restricted',
                  caption: `Anyone in ${orgName} can add, view, and reply to comments`
              }
          ]),
    {
        icon: <CommentsOff />,
        value: COMMENTS_STATUS_OPTIONS.off,
        caption: 'Turn off comments',
        label: `Comments off`
    }
]

const getSelectedValue = (value: PlaybookType['commentsStatus'], options: Array<optionType>) => {
    return options.find(option => option.value === value) || options[0]
}

const GuiddeListItem = withStyles({
    root: {
        '&:hover': {
            backgroundColor: '#F5F5F5'
        }
    }
})(ListItem)

type Props = {
    playbookId: string
    commentsStatus: PlaybookType['commentsStatus'] // playbook value
    onSave?: (value: Required<PlaybookType['commentsStatus']>) => void
}

export const EditCommentsPermission = ({ playbookId, commentsStatus, onSave }: Props) => {
    const { showSuccessNotification } = useNotification()

    const { enableComments } = useOrgFeatureFlags()

    const { comments, name } = useOrganization() // org value

    const { isPrivateAccount, uid } = useAuth()

    const isDisabled = comments === COMMENTS_STATUS_OPTIONS.off

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

    const options = useMemo(() => {
        return getCommentsOptions(name, isPrivateAccount)
    }, [name, isPrivateAccount])

    const [selectedValue, setSelectedValue] = useState(getSelectedValue(commentsStatus, options))

    const color = isDisabled ? '#d3d3d3' : '#090C10'

    useEffect(() => {
        // if disabled on org level set playbook level as off
        // if playbook comments were not set (commentsStatus), use the org level to set the initial value (comments)
        const value = isDisabled ? COMMENTS_STATUS_OPTIONS.off : commentsStatus || comments

        setSelectedValue(getSelectedValue(value, options))
    }, [commentsStatus, options, comments, isDisabled])

    if (!enableComments) return null

    return (
        <>
            <Box mb={1} fontSize={10} fontWeight="400" color="#7f8c9a">
                Comment settings
            </Box>
            <Box
                style={{ height: '45px', cursor: isDisabled ? 'default' : 'pointer' }}
                pt={0}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    if (isDisabled) return
                    setAnchorEl(e.currentTarget)
                }}
            >
                <SpacedGroup alignContent="center">
                    {selectedValue?.icon && <Box>{selectedValue?.icon}</Box>}
                    <SpacedGroup flexDirection="column" spacing={0.25}>
                        <SpacedGroup>
                            <Box
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color
                                }}
                            >
                                {selectedValue?.label}
                            </Box>
                            <ArrowDropDownIcon style={{ color }} />
                        </SpacedGroup>
                        <Box
                            style={{
                                fontSize: '14px',
                                fontWeight: 400,
                                color
                            }}
                        >
                            {selectedValue?.caption}
                        </Box>
                    </SpacedGroup>
                </SpacedGroup>
            </Box>
            <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} disablePortal>
                <Paper elevation={0}>
                    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                        <List style={{ cursor: 'pointer', padding: '8px' }}>
                            {options.map((listOption, idx) => (
                                <GuiddeListItem
                                    key={idx}
                                    divider={true}
                                    button={true}
                                    onClick={() => {
                                        if (selectedValue.value !== listOption.value) {
                                            setSelectedValue(
                                                getSelectedValue(listOption.value, options)
                                            )

                                            // update redux
                                            onSave?.(listOption.value)

                                            //update playbook
                                            updateComments({
                                                playbookId,
                                                commentsStatus: listOption.value
                                            })

                                            showSuccessNotification(
                                                COMMENTS_LABEL[listOption.value]
                                            )

                                            logToAnalytics('Comments', {
                                                eventName: `Playbook comment settings - ${listOption.value}`,
                                                user: uid,
                                                playbookId: playbookId
                                            })
                                        }

                                        setAnchorEl(null)
                                    }}
                                >
                                    <Box mr={0.5}>{listOption.icon}</Box>
                                    {listOption.label}
                                </GuiddeListItem>
                            ))}
                        </List>
                    </ClickAwayListener>
                </Paper>
            </Popover>
        </>
    )
}
